export interface NavItem {
  href: string
  label: string
  /** Duas letras para o modo recolhido. Monoespaçadas, alinham numa coluna. */
  code: string
  /**
   * Papel exigido dentro do escopo: `platformAdmin` (`platform_admin`) ou
   * `tenantAdmin` (`tenant_admin`). Fora da lista de quem não o tem — e a rota
   * também barra; a API responde 403 de todo jeito.
   */
  requires?: 'platformAdmin' | 'tenantAdmin'
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * O menu, na ordem em que o trabalho acontece.
 *
 * "Operação" primeiro porque é a tela que fica aberta o dia inteiro; "Gestão"
 * na ordem do fluxo de provisionamento (empresa → máquina → licença → agente),
 * que é a ordem em que a API obriga a criar as coisas e, invertida, a ordem em
 * que ela obriga a desfazê-las. Um menu em ordem alfabética esconderia essa
 * dependência.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Operação',
    items: [
      {
        href: '/',
        label: 'Console',
        code: 'CO',
      },
      {
        href: '/deteccoes',
        label: 'Detecções',
        code: 'DT',
      },
      {
        href: '/analise-ia',
        label: 'Análise por IA',
        code: 'IA',
      },
      {
        href: '/challenges',
        label: 'Challenges',
        code: 'CH',
      },
      {
        href: '/logs',
        label: 'Logs',
        code: 'LG',
      },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        href: '/empresas',
        label: 'Empresas',
        code: 'EM',
      },
      {
        href: '/maquinas',
        label: 'Máquinas',
        code: 'MQ',
      },
      {
        href: '/licencas',
        label: 'Licenças',
        code: 'LC',
      },
      {
        href: '/agentes',
        label: 'Agentes',
        code: 'AG',
      },
    ],
  },
  {
    label: 'Plataforma',
    items: [
      {
        href: '/usuarios',
        label: 'Usuários',
        code: 'US',
        requires: 'platformAdmin',
      },
      {
        href: '/conta',
        label: 'Minha conta',
        code: 'CT',
      },
    ],
  },
]

/**
 * O menu da empresa cliente (escopo `tenant`), sob o prefixo `/app`.
 *
 * Os endereços aqui são CANÔNICOS (`/deteccoes`, `/logs`): o roteador acrescenta
 * o prefixo da experiência. Não há Empresas, Licenças, console global nem
 * comandos — são da Black Ghost, e o backend responderia 403.
 */
export const TENANT_NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Segurança',
    items: [
      {
        href: '/',
        label: 'Visão geral',
        code: 'VG',
      },
      {
        href: '/deteccoes',
        label: 'Detecções',
        code: 'DT',
      },
      {
        href: '/analise-ia',
        label: 'Análise por IA',
        code: 'IA',
      },
      {
        href: '/logs',
        label: 'Logs',
        code: 'LG',
      },
    ],
  },
  {
    label: 'Ambiente',
    items: [
      {
        href: '/maquinas',
        label: 'Máquinas',
        code: 'MQ',
      },
      {
        href: '/agentes',
        label: 'Agentes',
        code: 'AG',
      },
    ],
  },
  {
    label: 'Conta',
    items: [
      {
        href: '/usuarios',
        label: 'Usuários',
        code: 'US',
        requires: 'tenantAdmin',
      },
      {
        href: '/conta',
        label: 'Minha conta',
        code: 'CT',
      },
    ],
  },
]

export function findNavItem(path: string, groups: readonly NavGroup[] = NAV_GROUPS): NavItem | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.href === path) return item
    }
  }
  return null
}
